import { useCallback, useEffect, useRef, useState } from 'react';
import { dashboardAi } from '../services/api';

export type DashboardAiVoiceStatus = 'idle' | 'recording' | 'transcribing' | 'error';

const MAX_DURATION_MS = 60_000;
const MIN_BLOB_BYTES = 512;

function pickRecorderMime(): string | undefined {
  if (typeof MediaRecorder === 'undefined') return undefined;
  return [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4',
    'audio/ogg;codecs=opus',
  ].find((mime) => MediaRecorder.isTypeSupported(mime));
}
export function useDashboardAiVoice({
  userId,
  locale,
  disabled,
  onTranscript,
  onError,
}: {
  userId: number | null;
  locale: string;
  disabled?: boolean;
  onTranscript: (text: string) => void;
  onError: () => void;
}) {
  const [status, setStatus] = useState<DashboardAiVoiceStatus>('idle');
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const stopTimeoutRef = useRef<number | null>(null);

  const cleanup = useCallback(() => {
    if (stopTimeoutRef.current != null) window.clearTimeout(stopTimeoutRef.current);
    stopTimeoutRef.current = null;
    mediaRecorderRef.current = null;
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    chunksRef.current = [];
  }, []);

  useEffect(() => () => cleanup(), [cleanup]);

  const isSupported =
    typeof navigator !== 'undefined'
    && !!navigator.mediaDevices?.getUserMedia
    && typeof MediaRecorder !== 'undefined';

  const upload = useCallback(async (blob: Blob) => {
    if (!userId || blob.size < MIN_BLOB_BYTES) {
      setStatus('error');
      onError();
      return;
    }
    setStatus('transcribing');
    const extension = blob.type.includes('mp4') ? 'm4a' : blob.type.includes('ogg') ? 'ogg' : 'webm';
    const formData = new FormData();
    formData.append('audio', blob, `dashboard-ai.${extension}`);
    formData.append('user_id', String(userId));
    formData.append('locale', locale.slice(0, 2));
    try {
      const response = await dashboardAi.transcribeAudio(formData);
      const transcript = response.data.data.transcript?.trim();
      if (!transcript) throw new Error('Empty transcript');
      onTranscript(transcript);
      setStatus('idle');
    } catch {
      setStatus('error');
      onError();
    }
  }, [locale, onError, onTranscript, userId]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current?.state === 'recording') mediaRecorderRef.current.stop();
  }, []);

  const startRecording = useCallback(async () => {
    if (disabled || !isSupported || !userId) {
      setStatus('error');
      onError();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mimeType = pickRecorderMime();
      const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      mediaRecorderRef.current = recorder;
      chunksRef.current = [];
      recorder.ondataavailable = (event) => {
        if (event.data.size) chunksRef.current.push(event.data);
      };
      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, {
          type: recorder.mimeType || mimeType || 'audio/webm',
        });
        cleanup();
        void upload(blob);
      };
      recorder.onerror = () => {
        cleanup();
        setStatus('error');
        onError();
      };
      recorder.start(250);
      setStatus('recording');
      stopTimeoutRef.current = window.setTimeout(stopRecording, MAX_DURATION_MS);
    } catch {
      cleanup();
      setStatus('error');
      onError();
    }
  }, [cleanup, disabled, isSupported, onError, stopRecording, upload, userId]);

  const toggleRecording = useCallback(() => {
    if (status === 'recording') stopRecording();
    else void startRecording();
  }, [startRecording, status, stopRecording]);

  return {
    status,
    isSupported,
    isRecording: status === 'recording',
    isTranscribing: status === 'transcribing',
    toggleRecording,
  };
}
